// ABOUTME: Canonical platform slug -> display metadata (label, brand icon and
// colours, connections-page copy). The ONE table every user-facing platform
// name in the frontend reads from — mirrors PLATFORM_DISPLAY_NAMES in
// app/services/social/display.py, and the two must stay in agreement.

import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Music2,
  Image as ImageIcon,
  MapPin,
  Globe,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { XIcon } from '@/components/platform-logos/XIcon'
import { PLATFORM_FORMAT_MAP } from './platformValidation'

export interface PlatformInfo {
  label: string
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  /** Tailwind background for a branded avatar pill. */
  bg: string
  /** Tailwind foreground for a branded avatar pill. */
  fg: string
  /** Short glyph for chips too small to read, where lucide has no brand mark. */
  monogram?: string
  /** Connections-page copy; only platforms offered there carry one. */
  description?: string
  /**
   * What the platform's own API will NOT let a business connect (#1970).
   *
   * These are permanent Meta rules, not Maxflow limitations and not something
   * a scope change fixes — stating them on the card is the only honest answer
   * to "why can't I connect my personal Facebook?". MUST stay byte-identical
   * to PLATFORM_CONNECTION_RESTRICTIONS in
   * app/services/social/platform_capabilities.py, which is what the chat agent
   * and the MCP account tools say; the two are compared by
   * tests/unit/test_platform_restriction_copy_parity.py so the UI and the
   * assistant cannot drift into two different explanations.
   */
  restriction?: string
}

export const PLATFORMS: Record<string, PlatformInfo> = {
  instagram: {
    label: 'Instagram',
    icon: Instagram,
    bg: 'bg-gradient-to-br from-brand-instagram-from via-brand-instagram-via to-brand-instagram-to',
    fg: 'text-white',
    description: 'Schedule posts, stories, and reels',
    restriction:
      "Instagram Business or Creator accounts only, and the account must be linked to a Facebook Page — that is Meta's requirement for its publishing API, not ours. A personal Instagram account cannot be connected. Switch account type in the Instagram app under Settings → Account type and tools, link it to your Page, then connect.",
  },
  facebook: {
    label: 'Facebook',
    icon: Facebook,
    bg: 'bg-brand-facebook',
    fg: 'text-white',
    description: 'Share posts and updates to your page',
    restriction:
      'Facebook Pages only. Meta removed personal-profile publishing from its API in 2018 and never replaced it, so no app — including Maxflow — can post to a personal profile. Creating a Facebook Page is free and takes a couple of minutes; connect the Page instead.',
  },
  twitter: {
    label: 'X',
    icon: XIcon,
    bg: 'bg-black',
    fg: 'text-white',
    description: 'Post tweets and threads',
  },
  linkedin: {
    label: 'LinkedIn',
    icon: Linkedin,
    bg: 'bg-brand-linkedin',
    fg: 'text-white',
    description: 'Share updates to your professional network',
  },
  tiktok: {
    label: 'TikTok',
    icon: Music2,
    bg: 'bg-black',
    fg: 'text-white',
    monogram: 'TT',
    description: 'Publish short-form video content',
  },
  youtube: {
    label: 'YouTube',
    icon: Youtube,
    bg: 'bg-brand-youtube',
    fg: 'text-white',
    description: 'Upload videos and shorts',
  },
  pinterest: {
    label: 'Pinterest',
    icon: ImageIcon,
    bg: 'bg-brand-pinterest',
    fg: 'text-white',
    monogram: 'Pin',
    description: 'Create pins and boards',
  },
  gmb: {
    label: 'Google Business Profile',
    icon: MapPin,
    bg: 'bg-brand-google-blue',
    fg: 'text-white',
    monogram: 'GB',
  },
}

// Display-only aliases. GMB reaches the UI under three slugs: drafts use
// `gmb`, ConnectedAccount rows use `google_gmb`, and the OAuth callback
// returns `google`. Resolution is deliberately confined to the display
// helpers — `isPostPlatform` stays strict, because `google_gmb` is NOT a
// valid ContentDraft.platforms value.
const PLATFORM_ALIASES: Record<string, string> = {
  google: 'gmb',
  google_gmb: 'gmb',
  x: 'twitter',
}

/**
 * Canonical slug for a platform, collapsing the display aliases above.
 *
 * Exported because grouping and filtering need the SAME collapse the labels
 * do: rows stored under `x` and under `twitter` both render as "X", so a
 * channel filter that compares raw slugs splits one channel into two
 * identically-labelled options, each catching only half the content (#2238).
 * `isPostPlatform` deliberately stays strict — see its doc comment.
 */
export function resolvePlatformSlug(platform: string): string {
  const key = platform.trim().toLowerCase()
  return PLATFORM_ALIASES[key] ?? key
}

const FALLBACK_BRAND: Omit<PlatformInfo, 'label'> = {
  icon: Globe,
  bg: 'bg-muted',
  fg: 'text-foreground',
}

/**
 * Human-readable name for a platform slug — never the raw slug.
 *
 * Unknown slugs degrade to a de-underscored, title-cased form, matching the
 * backend's `platform_display_name()`, so a newly supported platform reads as
 * "Meta Ads" rather than "meta_ads" before it earns a table entry.
 */
export function platformLabel(platform: string): string {
  const canonical = resolvePlatformSlug(platform)
  const known = PLATFORMS[canonical]?.label
  if (known) return known
  return canonical
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/** Label + icon + brand colours for a branded avatar pill. */
export function getPlatformBrand(platform: string): PlatformInfo {
  const known = PLATFORMS[resolvePlatformSlug(platform)]
  if (known) return known
  return { ...FALLBACK_BRAND, label: platformLabel(platform) }
}

/** Connections-page blurb, or an empty string for platforms without one. */
export function platformDescription(platform: string): string {
  return PLATFORMS[resolvePlatformSlug(platform)]?.description ?? ''
}

/**
 * What this platform's API refuses to connect, or '' when it has no such rule.
 *
 * See `PlatformInfo.restriction` — the string is mirrored from the backend and
 * must not be reworded here alone.
 */
export function platformRestriction(platform: string): string {
  return PLATFORMS[resolvePlatformSlug(platform)]?.restriction ?? ''
}

/** Caption character budget, from the canonical capability matrix. */
export function platformMaxLength(platform: string): number {
  return PLATFORM_FORMAT_MAP[resolvePlatformSlug(platform)]?.maxLength ?? 0
}

/**
 * Whether a slug is a social post target.
 *
 * A business can connect ads / calendar / commerce accounts too — those are
 * not post targets and their slugs (`google_ads`, `google_gmb`) aren't valid
 * draft platforms, so membership is checked WITHOUT alias resolution.
 */
export function isPostPlatform(platform: string): boolean {
  return platform in PLATFORMS
}

export interface PlatformCompatibility {
  compatible: boolean
  /** Reason a platform CAN'T post (hard block — disabled, unselected). */
  blockedReason?: string
  /** Soft note when posting is allowed but suboptimal. */
  warning?: string
}

/**
 * Compute whether a profile/platform can publish the given content.
 * Mirrors validateContentForPlatforms() but per-platform with hard/soft split:
 *   - hard block (compatible: false): requiresMedia w/o media, requiresVideo
 *     w/o video, format unsupported. The platform is disabled in the picker.
 *   - soft warning (compatible: true): media-type listed but suboptimal.
 */
export function platformCompatibility(
  platform: string,
  contentType: string,
  format: string | null,
  hasMedia: boolean
): PlatformCompatibility {
  const rules = PLATFORM_FORMAT_MAP[platform.toLowerCase()]
  if (!rules) return { compatible: true }

  const label = platformLabel(platform)

  if (rules.requiresVideo && contentType !== 'video') {
    return { compatible: false, blockedReason: `${label} only accepts videos` }
  }
  if (rules.requiresMedia && !hasMedia) {
    return { compatible: false, blockedReason: `${label} needs an image or video` }
  }
  if (format && !rules.formats.includes(format)) {
    return {
      compatible: false,
      blockedReason: `${label} doesn't support ${format}`,
    }
  }
  if (!rules.media.includes(contentType)) {
    return { compatible: true, warning: `${label} has limited ${contentType} support` }
  }
  return { compatible: true }
}
